import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ncbService } from '../services/ncbService';

const { FiFacebook, FiMapPin, FiPhone, FiMail, FiLock, FiHome, FiHeart } = FiIcons;

const Footer = () => {
  const location = useLocation();
  const isRatesPage = location.pathname === '/rates';
  const [settings, setSettings] = useState({});
  const [profile, setProfile] = useState({});

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        // Use Promise.allSettled to ensure one failure doesn't block the other
        const [settingsResult, profileResult] = await Promise.allSettled([
          ncbService.getSiteSettings(),
          ncbService.getBusinessProfile()
        ]);

        if (settingsResult.status === 'fulfilled') {
          const settingsMap = settingsResult.value.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
          setSettings(settingsMap);
        }

        if (profileResult.status === 'fulfilled' && profileResult.value && profileResult.value.length > 0) {
          setProfile(profileResult.value[0]);
        }
      } catch (err) {
        console.warn("Failed to load footer data, using fallbacks", err);
      }
    };
    fetchFooterData();
  }, []);

  /**
   * Tiered Fallback Helper
   * 1. Business Profile (New central truth)
   * 2. Site Settings (Legacy)
   * 3. Hardcoded Fallback
   */
  const getVal = (profileKey, settingsKey, fallback) => {
    return profile[profileKey] || settings[settingsKey] || fallback;
  };

  const BOOKING_URL = "https://www.booking.com/hotel/ph/rvenue-apartelle.en-gb.html?aid=356980&label=gog235jc-10CAsotAFCEHJ2ZW51ZS1hcGFydGVsbGVIM1gDaLQBiAEBmAEzuAEXyAEM2AED6AEB-AEBiAIBqAIBuAK5jcvPBsACAdICJDc3ZDM0MDYzLTU3YTctNDQ3YS04ZGVmLTg4Yzg5NTNmOGMwMtgCAeACAQ&sid=2692b10c8ee6454563bfa8f85d5c2dd2&dist=0&keep_landing=1&sb_price_type=total&type=total&chal_t=1777518266171&force_referer=https%3A%2F%2Fwww.google.com%2F";

  // Address formatting helper to split the long address string into readable lines
  const renderFormattedAddress = () => {
    const rawAddress = getVal('address', 'address', '3rd Floor R.Venue Bldg., Quezon Ave Cor Avenida Rizal, Lucban, Quezon 4328, PH');
    const parts = rawAddress.split(',').map(p => p.trim()).filter(Boolean);
    
    if (parts.length <= 1) return rawAddress;

    return (
      <>
        {parts[0]}<br />
        {parts[1]}<br />
        {parts.slice(2).join(', ')}
      </>
    );
  };

  return (
    <footer className="w-full">
      {!isRatesPage && (
        <div className="bg-white border-t border-brand-olive/5">
          <div className="container mx-auto px-6 py-24 text-center">
            <p className="font-script text-3xl text-brand-olive mb-2">Your Home in Lucban</p>
            <h2 className="font-serif text-3xl md:text-5xl uppercase tracking-[0.1em] text-brand-olive mb-10 max-w-3xl mx-auto leading-tight">
              Discover the comfort of home <br className="hidden md:block" /> with a touch of modern luxury at R. Venue.
            </h2>
            <Link to="/rates" className="btn-primary">
              Check Availability
            </Link>
          </div>
        </div>
      )}

      <div className="bg-brand-olive text-white">
        <div className="container mx-auto px-6 pt-20 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 lg:gap-8">
            <div className="md:col-span-5 lg:pr-20">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/bc4ab284ada283515beb028c73672da2.png" 
                alt="R. Venue Logo" 
                className="w-48 mb-8 object-contain -ml-1 opacity-90" 
              />
              <p className="text-white/70 font-light leading-relaxed mb-8 max-w-sm">
                {settings['footer_text'] || 'A clean, welcoming, and nature-inspired getaway in the heart of Lucban, Quezon. Experience modern boutique comfort with authentic Filipino hospitality.'}
              </p>
              <div className="flex space-x-4">
                <a href={getVal('facebook_url', 'facebook_url', 'https://www.facebook.com/p/R-Venue-Apartelle-100092378052113/')} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/20 transition-all border border-white/10">
                  <SafeIcon icon={FiFacebook} className="text-white text-sm" />
                </a>
                <Link to="/" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/20 transition-all border border-white/10">
                  <SafeIcon icon={FiHome} className="text-white text-sm" />
                </Link>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/20 transition-all border border-white/10">
                  <span className="text-white font-serif font-bold text-sm">B.</span>
                </a>
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="font-serif text-sm mb-10 text-white/50 tracking-[0.3em] uppercase font-bold">Quick Links</h4>
              <ul className="space-y-4 text-white/80 font-light text-sm">
                <li><Link to="/" className="hover:text-white transition-all inline-block hover:translate-x-1">Home</Link></li>
                <li><Link to="/stay" className="hover:text-white transition-all inline-block hover:translate-x-1">The Stay</Link></li>
                <li><Link to="/gallery" className="hover:text-white transition-all inline-block hover:translate-x-1">Gallery</Link></li>
                <li><Link to="/location" className="hover:text-white transition-all inline-block hover:translate-x-1">Location</Link></li>
                <li><Link to="/rates" className="hover:text-white transition-all inline-block hover:translate-x-1">Rates & Booking</Link></li>
              </ul>
            </div>

            <div className="md:col-span-4">
              <h4 className="font-serif text-sm mb-10 text-white/50 tracking-[0.3em] uppercase font-bold">Inquiries</h4>
              <ul className="space-y-6 text-white/80 font-light text-sm">
                <li className="flex items-start gap-4">
                  <SafeIcon icon={FiMapPin} className="mt-1 flex-shrink-0 text-white/40" />
                  <span className="leading-relaxed">
                    {renderFormattedAddress()}
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <SafeIcon icon={FiPhone} className="mt-1 flex-shrink-0 text-white/40" />
                  <div className="flex flex-col gap-1">
                    <a href={`tel:${getVal('phone_primary', 'contact_phone_1', '+639176818251').replace(/\s+/g, '')}`} className="hover:text-white transition-colors">
                      {getVal('phone_primary', 'contact_phone_1', '+63 917 681 8251')}
                    </a>
                    <a href={`tel:${getVal('phone_secondary', 'contact_phone_2', '+639763409846').replace(/\s+/g, '')}`} className="hover:text-white transition-colors">
                      {getVal('phone_secondary', 'contact_phone_2', '+63 976 340 9846')}
                    </a>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <SafeIcon icon={FiMail} className="flex-shrink-0 text-white/40" />
                  <a href={`mailto:${getVal('email', 'contact_email', 'rvenuespaces@gmail.com')}`} className="hover:text-white transition-colors">
                    {getVal('email', 'contact_email', 'rvenuespaces@gmail.com')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-black/10">
          <div className="container mx-auto px-6 py-10">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-8 text-[10px] tracking-widest font-bold uppercase">
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 order-2 lg:order-1">
                <p className="text-white/40">&copy; {new Date().getFullYear()} {getVal('business_name', 'site_name', 'R. VENUE Apartelle')}</p>
                <div className="hidden md:block w-px h-3 bg-white/10" />
                <div className="flex items-center gap-6 text-white/30">
                  <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms & Conditions</Link>
                </div>
              </div>

              <div className="flex items-center gap-2 text-white/50 bg-white/5 px-6 py-2 rounded-full border border-white/5 order-1 lg:order-2">
                <span className="font-light">Made with</span>
                <SafeIcon icon={FiHeart} className="text-white fill-white text-[10px] animate-pulse" />
                <span className="font-black">by Digital Revenue</span>
              </div>

              <div className="order-3">
                <Link to="/admin/login" className="flex items-center gap-3 px-6 py-3 border border-white/10 hover:border-white hover:bg-white hover:text-brand-olive transition-all duration-500 rounded-sm group">
                  <SafeIcon icon={FiLock} className="text-xs text-white/40 group-hover:text-brand-olive" />
                  <span className="text-[9px] font-black tracking-[0.25em]">Admin Portal</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;